import axios, { AxiosError, AxiosInstance } from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.xuman.ai';
const SUPPORT_API_URL = process.env.REACT_APP_SUPPORT_API_URL || `${API_BASE_URL}/support`;
const SERVICES_API_URL = process.env.REACT_APP_SERVICES_API_URL || `${API_BASE_URL}/services`;

// Azure APIM Subscription Key (required for api.xuman.ai).
// Must be provided at build time via REACT_APP_APIM_SUBSCRIPTION_KEY — never hardcode it.
const APIM_SUBSCRIPTION_KEY = process.env.REACT_APP_APIM_SUBSCRIPTION_KEY || '';
if (!APIM_SUBSCRIPTION_KEY) {
  // eslint-disable-next-line no-console
  console.error(
    'REACT_APP_APIM_SUBSCRIPTION_KEY is not set — all API requests will be rejected by APIM. ' +
      'Add it to .env (local) or the deploy secrets (CI).'
  );
}

// Create axios instance with defaults
const createApiClient = (baseURL: string): AxiosInstance => {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': APIM_SUBSCRIPTION_KEY,
    },
    timeout: 30000,
  });

  // Request interceptor - add auth token
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle errors
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      // Only logout on 401 if we're not using demo token
      // This prevents redirect loops during development
      if (error.response?.status === 401) {
        const token = localStorage.getItem('authToken');
        // Don't auto-logout for demo tokens or during initial load
        if (token && !token.startsWith('demo-')) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('isAuthenticated');
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
};

export const api = createApiClient(API_BASE_URL);
export const supportApi = createApiClient(SUPPORT_API_URL);
export const servicesApi = createApiClient(SERVICES_API_URL);

export default api;
